import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, BadgeCheckIcon, CreditCardIcon, PhoneIcon } from 'lucide-react';
interface HeroProps {
  onCtaClick: () => void;
}
export function Hero({
  onCtaClick
}: HeroProps) {
  return <section className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-brand-navy via-[#0d2a52] to-brand-deep" data-id="element-108">
      {/* Enhanced decorative elements with gold accents */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-109">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl" data-id="element-110" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-aqua/20 rounded-full blur-3xl" data-id="element-111" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-gold/5 rounded-full blur-3xl" data-id="element-112" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 min-h-screen flex flex-col" data-id="element-113">
        {/* Premium Header Navigation */}
        <motion.nav initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex items-center justify-between py-6 mb-8" data-id="element-114">
          {/* Professional Logo + Wordmark */}
          <motion.div className="flex items-center gap-3 sm:gap-4" whileHover={{
          scale: 1.02
        }} transition={{
          duration: 0.2
        }} data-id="element-115">
            {/* Logo Icon */}
            <img src="https://cdn.magicpatterns.com/uploads/1q4zYkKzzt1SxjYXmqf17t/dermaclinic_logo.png" alt="Dermaclinic" className="h-10 sm:h-12 lg:h-14 w-auto flex-shrink-0" data-id="element-116" />

            {/* Wordmark with proper spacing */}
            <div className="relative" data-id="element-117">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-[0.02em] text-white leading-none" data-id="element-118">
                <span className="font-bold" data-id="element-119">DERMA</span>
                <span className="font-serif italic text-brand-gold ml-1" data-id="element-120">
                  clinic
                </span>
              </h2>
              <div className="absolute -bottom-0.5 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" data-id="element-121" />
            </div>
          </motion.div>

          {/* Premium CTA Button - Mobile Optimized */}
          <motion.button onClick={onCtaClick} whileTap={{
          scale: 0.95
        }} className="relative overflow-hidden px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-brand-gold via-[#e0b456] to-brand-gold rounded-full shadow-lg shadow-brand-gold/30 transition-all duration-300 hover:shadow-xl hover:shadow-brand-gold/50 active:shadow-brand-gold/60" data-id="element-122">
            <span className="relative z-10 flex items-center gap-2 text-brand-navy font-bold text-sm sm:text-base whitespace-nowrap" data-id="element-123">
              <PhoneIcon className="w-4 h-4" data-id="element-124" />
              <span data-id="element-125">Book Now</span>
            </span>
          </motion.button>
        </motion.nav>

        {/* Main content grid - 2 columns on desktop */}
        <div className="flex-1 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-126">
          {/* Left column - Text content */}
          <div data-id="element-127">
            {/* Main headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.15] mb-6" data-id="element-128">
              Transform Your Body With the{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary via-brand-gold to-brand-primary" data-id="element-129">
                Most Advanced
              </span>{' '}
              Aesthetic Treatments in the U.S.
            </motion.h1>

            {/* Subheadline */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="text-lg sm:text-xl text-white/80 flex flex-wrap items-center gap-3 sm:gap-4 mb-10" data-id="element-130">
              <span className="flex items-center gap-2" data-id="element-131">
                <span className="w-1.5 h-1.5 bg-brand-gold rounded-full shadow-lg shadow-brand-gold/50" data-id="element-132" />
                <span className="font-light" data-id="element-133">Safe</span>
              </span>
              <span className="flex items-center gap-2" data-id="element-134">
                <span className="w-1.5 h-1.5 bg-brand-gold rounded-full shadow-lg shadow-brand-gold/50" data-id="element-135" />
                <span className="font-light" data-id="element-136">Certified Specialists</span>
              </span>
              <span className="flex items-center gap-2" data-id="element-137">
                <span className="w-1.5 h-1.5 bg-brand-gold rounded-full shadow-lg shadow-brand-gold/50" data-id="element-138" />
                <span className="font-light" data-id="element-139">10,000+ Happy Patients</span>
              </span>
            </motion.p>

            {/* CTA Button - Mobile Optimized & Elegant */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="mb-12" data-id="element-140">
              <motion.button onClick={onCtaClick} whileTap={{
              scale: 0.98
            }} className="group relative w-full sm:w-auto overflow-hidden px-8 py-4 sm:px-12 sm:py-5 bg-gradient-to-r from-brand-gold via-[#e0b456] to-brand-gold rounded-full shadow-2xl shadow-brand-gold/40 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/60" aria-label="Claim your free consultation" data-id="element-141">
                <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3 text-brand-navy font-bold text-base sm:text-lg" data-id="element-142">
                  <span className="whitespace-nowrap" data-id="element-143">
                    Claim Your Free Consultation
                  </span>
                  <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:translate-x-1" data-id="element-144">
                    →
                  </span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#c99435] to-brand-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-145" />
              </motion.button>
            </motion.div>

            {/* Premium Trust badges - Minimal & Elegant */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="flex flex-wrap gap-3 sm:gap-4" data-id="element-146">
              <div className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-147">
                <ShieldCheckIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-gold" data-id="element-148" />
                <span className="text-xs text-white/90 font-light" data-id="element-149">
                  FDA-Approved
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-150">
                <BadgeCheckIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-gold" data-id="element-151" />
                <span className="text-xs text-white/90 font-light" data-id="element-152">
                  Board-Certified
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-153">
                <CreditCardIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-gold" data-id="element-154" />
                <span className="text-xs text-white/90 font-light" data-id="element-155">
                  Financing Available
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right column - Clinic image with luxury styling */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="relative" data-id="element-156">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-brand-gold/20" data-id="element-157">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/b9rr6PQk9i7eBVBRDdWDaK/Screenshot_2025-12-03_at_9.23.41_PM.png" alt="Dermaclinic luxury reception area" className="w-full h-auto object-cover" data-id="element-158" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-transparent to-transparent" data-id="element-159" />

              {/* Elegant badge overlay */}
              <div className="absolute top-6 left-6 px-5 py-2 bg-brand-gold/95 backdrop-blur-sm rounded-full shadow-xl" data-id="element-160">
                <p className="text-brand-navy font-semibold text-sm tracking-wide" data-id="element-161">
                  Premium Facilities
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}